import { ShieldCheck, Crosshair, Users, Clock, Handshake } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { Tilt } from "@/components/Tilt";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1504846257989-a76209d9d2ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxzZW1pJTIwdHJ1Y2slMjBuaWdodCUyMGhpZ2h3YXklMjBkdXNrJTIwZnJlaWdodHxlbnwwfHx8fDE3OTI2MDc1NzZ8MA&ixlib=rb-4.1.0&q=85";

const VALUES = [
  { icon: ShieldCheck, title: "Integrity", desc: "Doing business with honesty and transparency." },
  { icon: Crosshair, title: "Precision", desc: "Paying attention to the details that matter." },
  { icon: Users, title: "Diversity", desc: "Respecting and supporting carriers from all backgrounds." },
  { icon: Clock, title: "Reliability", desc: "Providing consistent communication and dependable service." },
  { icon: Handshake, title: "Partnership", desc: "Treating every carrier's business with care." },
];

export const About = () => (
  <section id="about" data-testid="about-section" className="py-20 lg:py-32 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <Reveal className="relative">
        <div className="absolute -top-4 -left-4 w-full h-full border border-gold/40 pointer-events-none" />
        <div className="relative overflow-hidden">
          <img
            src={ABOUT_IMG}
            alt="Heavy-duty semi-truck crossing a bridge at dawn"
            className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
        </div>
      </Reveal>

      <div>
        <SectionHeading overline="About Us" title="More Than Dispatching. We're Your Partner on the Road." testId="about-heading" tag="Partner on the Road!" />
        <Reveal delay={0.1}>
          <p className="text-zinc-400 text-sm sm:text-base leading-relaxed mb-5">
            At B&amp;H Truck Dispatching, we believe successful trucking operations require more than simply finding
            loads. They require communication, strategic planning, dependable support, and a dispatching partner who
            understands the challenges carriers face every day.
          </p>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="text-zinc-400 text-sm sm:text-base leading-relaxed mb-5">
            Our mission is to help owner-operators and trucking companies operate with confidence by providing precision
            dispatching and personalized service.
          </p>
        </Reveal>
        <Reveal delay={0.26}>
          <p className="text-zinc-400 text-sm sm:text-base leading-relaxed mb-8">
            We are committed to building long-term relationships rooted in integrity, diversity, transparency, and a
            genuine dedication to our carriers' success.
          </p>
        </Reveal>
        <Reveal delay={0.32}>
          <blockquote className="border-l-2 border-gold pl-6 py-1">
            <p className="font-accent text-champagne text-base sm:text-lg leading-relaxed">
              “To drive logistics forward by empowering carriers with precision dispatching, built on a foundation of
              integrity, diversity, and unmatched support.”
            </p>
          </blockquote>
        </Reveal>
      </div>
    </div>

    <div className="mt-20 lg:mt-28">
      <Reveal>
        <p className="overline-label mb-10 text-center">Our Core Values</p>
      </Reveal>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-6">
        {VALUES.map((v, i) => (
          <Reveal key={v.title} delay={0.08 * i} className="h-full">
            <Tilt max={9} className="h-full">
              <div
                data-testid={`value-card-${v.title.toLowerCase()}`}
                className="card-gold p-6 h-full text-center group"
              >
                <v.icon size={30} className="mx-auto text-gold mb-4 group-hover:scale-110 transition-transform duration-300" />
                <h3 className="font-display text-lg uppercase tracking-wide text-white mb-2">{v.title}</h3>
                <p className="text-xs text-zinc-400 leading-relaxed">{v.desc}</p>
              </div>
            </Tilt>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
